import { apiRequest, queryClient } from "@/lib/queryClient";
import { isFriendLimitError, errorMessage } from "@/lib/apiError";

// b20 (Sam 2026-07-02): shared add-friend redemption. Extracted from
// DeepLinkHandler so the live QR scanner (Friends → Scan tab) and the OS
// deep-link path run the exact same redemption logic against
// POST /api/friends/qr-redeem. A scanned QR encodes a
// `nexusdex://add-friend?token=...` (or https universal-link) URL, so both
// entry points hand the raw URL here.
//
// Viral engine: this module also understands the shared INVITE link,
// `https://nexusdex.ai/friends/redeem?token=CODE` (buildFriendsRedeemUrl in
// the Worker). That token is a friend-invite CODE, redeemed through
// POST /api/friends/redeem (the Add Friend modal's Code tab endpoint), not
// the HMAC qr-redeem path. Universal links hand these URLs to the app when
// it is already installed; without this branch they parsed as "invalid".

export type FriendInviteToast = (opts: {
  title: string;
  description?: string;
  variant?: "default" | "destructive";
}) => void;

export interface RedeemFriendUrlDeps {
  /** True when the scanning user is signed in. */
  isSignedIn: boolean;
  toast: FriendInviteToast;
  /** wouter setLocation (or any (path: string) => void navigator). */
  navigate: (path: string) => void;
  /**
   * Called instead of the error toast when the server refuses because the
   * scanner is at the Basic friend cap. Receives the server's own explanation
   * so the cap copy lives in one place. Optional so non-UI callers can omit it
   * and fall back to a plain toast.
   */
  onFriendLimit?: (message: string) => void;
  /**
   * Called when an invite-code redeem paid out a Pro grant (viral engine), so
   * the caller can re-pull entitlements and unlock gates immediately: matches
   * the Add Friend modal's refreshEntitlements() on proGranted.
   */
  onProGranted?: () => void;
}

export type RedeemResult =
  | { status: "redeemed" }
  | { status: "stashed" } // not signed in; token saved for post-login
  | { status: "invalid" } // url had no add-friend token
  | { status: "friend_limit" } // Basic user at their connection cap
  | { status: "error" };

const PENDING_TOKEN_KEY = "nexusdex.pendingFriendToken";
const PENDING_CODE_KEY = "nexusdex.pendingInviteCode";

/**
 * Parse an add-friend URL and redeem its token. Safe to call from either the
 * Capacitor appUrlOpen listener or the in-app QR scanner. Returns a status so
 * callers (e.g. the scanner) can decide whether to keep the camera open.
 */
export async function redeemFriendUrl(
  url: string,
  deps: RedeemFriendUrlDeps,
): Promise<RedeemResult> {
  const { isSignedIn, toast, navigate, onFriendLimit } = deps;

  // Shared invite link (universal link): the token IS the invite code.
  const redeemMatch = url.match(/friends\/redeem\?(.+)$/);
  if (redeemMatch) {
    const code = new URLSearchParams(redeemMatch[1]).get("token");
    if (!code) return { status: "invalid" };
    return redeemInviteCode(code, deps);
  }

  // Accept both the custom scheme and an https universal-link form.
  const match = url.match(/add-friend\?(.+)$/);
  if (!match) return { status: "invalid" };
  const params = new URLSearchParams(match[1]);
  const token = params.get("token");
  const handle = params.get("handle");
  if (!token) return { status: "invalid" };

  if (!isSignedIn) {
    try {
      sessionStorage.setItem(PENDING_TOKEN_KEY, token);
    } catch {
      /* private mode — ignore */
    }
    toast({
      title: "Sign in to add this trainer",
      description: handle ? `@${handle} is waiting in your invites.` : undefined,
    });
    navigate("/friends");
    return { status: "stashed" };
  }

  try {
    const res = await apiRequest("POST", "/api/friends/qr-redeem", { token });
    const data = (await res.json()) as {
      ok?: boolean;
      alreadyFriends?: boolean;
      autoAccepted?: boolean;
      alreadyPending?: boolean;
    };
    queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
    queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
    const title = data.alreadyFriends
      ? "Already friends"
      : data.autoAccepted
        ? "You're now friends"
        : "Request sent";
    const description = data.alreadyFriends
      ? "You're already connected with that trainer."
      : data.autoAccepted
        ? handle ? `Connected with @${handle}.` : "Connected."
        : handle ? `@${handle} will see your request.` : "They'll see your request.";
    toast({ title, description });
    return { status: "redeemed" };
  } catch (err) {
    // At the Basic friend cap: hand off to the paywall rather than blaming the
    // invite link, which is fine — it is the cap that refused.
    if (isFriendLimitError(err)) {
      if (onFriendLimit) {
        onFriendLimit(errorMessage(err));
      } else {
        toast({ title: "Friend limit reached", description: errorMessage(err), variant: "destructive" });
      }
      return { status: "friend_limit" };
    }
    toast({
      title: "Couldn't add that trainer",
      description: err instanceof Error ? errorMessage(err) : "The invite link may have expired.",
      variant: "destructive",
    });
    return { status: "error" };
  }
}

/**
 * Redeem a friend-invite CODE (the `?token=` of a shared
 * nexusdex.ai/friends/redeem link) through POST /api/friends/redeem.
 * Mirrors the Add Friend modal's Code tab: same endpoint, same toasts, same
 * paywall handoff at the friend cap. isBrandNewSignup stays false here: the
 * server derives brand-new from account age (7-day window), which is the
 * path that actually covers link-installed friends.
 */
async function redeemInviteCode(
  code: string,
  deps: RedeemFriendUrlDeps,
): Promise<RedeemResult> {
  const { isSignedIn, toast, navigate, onFriendLimit, onProGranted } = deps;

  if (!isSignedIn) {
    try {
      sessionStorage.setItem(PENDING_CODE_KEY, code);
    } catch {
      /* private mode — ignore */
    }
    toast({
      title: "Sign in to accept your invite",
      description: "Your friend's invite code is saved. It applies once you're signed in.",
    });
    navigate("/friends");
    return { status: "stashed" };
  }

  try {
    const res = await apiRequest("POST", "/api/friends/redeem", {
      code,
      isBrandNewSignup: false,
    });
    const data = (await res.json()) as {
      ok?: boolean;
      isReferral?: boolean;
      storageGranted?: boolean;
      proGranted?: boolean;
      proDays?: number;
    };
    queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
    queryClient.invalidateQueries({ queryKey: ["/api/friends/referrals"] });
    if (data.proGranted) onProGranted?.();
    const description = data.proGranted
      ? `You joined from a friend's invite: ${data.proDays ?? 7} days of Nexus Dex Pro are now unlocked.`
      : "You'll see them in your friends list shortly.";
    toast({ title: data.proGranted ? "Friend added: Pro unlocked" : "Friend added", description });
    navigate("/friends");
    return { status: "redeemed" };
  } catch (err) {
    if (isFriendLimitError(err)) {
      if (onFriendLimit) {
        onFriendLimit(errorMessage(err));
      } else {
        toast({ title: "Friend limit reached", description: errorMessage(err), variant: "destructive" });
      }
      return { status: "friend_limit" };
    }
    toast({
      title: "Couldn't redeem that invite",
      description: err instanceof Error ? errorMessage(err) : "The invite link may have expired.",
      variant: "destructive",
    });
    return { status: "error" };
  }
}

/** Redeem any token stashed while the user was logged out. Call once signed in. */
export function takePendingFriendToken(): string | null {
  try {
    const pending = sessionStorage.getItem(PENDING_TOKEN_KEY);
    if (pending) {
      sessionStorage.removeItem(PENDING_TOKEN_KEY);
      return pending;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Redeem any invite CODE stashed while the user was logged out. */
export function takePendingInviteCode(): string | null {
  try {
    const pending = sessionStorage.getItem(PENDING_CODE_KEY);
    if (pending) {
      sessionStorage.removeItem(PENDING_CODE_KEY);
      return pending;
    }
  } catch {
    /* ignore */
  }
  return null;
}
